import { ButtonPrimary, ButtonTertiary } from "components/Atoms/Button";
import React, { useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { useApi } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader, PreviewPanelHorizontalDivider } from "../PreviewPanel";
import AppointmentPhase from "components/Atoms/AppointmentPhase";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import { PreviewPanelDepartureArrival } from "components/Atoms/details/PreviewPanelDepartureArrival";
import { JourneySegment } from "components/Atoms/JourneySegment";
import { formatTime, formatWrittenDayMonthYear } from "utilities/momentutils";
import { ArtistPreview } from "../Company/CompanyPreviewPanel";
import { PreviewPanelContactList } from "../contact/PreviewPanelContactList";

interface TrainSegmentPreviewPanelProps {
  trainSegmentId: number
}

const TrainSegmentPreviewPanel = (props: TrainSegmentPreviewPanelProps) => {
  const { trainSegmentId } = props

  const api = useApi(() => window.Api.Trains.GetTrainBySegment(trainSegmentId), ["train-segment", trainSegmentId], {
    startEnabled: true
  });

  useEffect(() => {
    api.refetch();
  }, [trainSegmentId]);

  if (api.isInitialLoading) {
    return <>
      <PreviewPanelHeader title={RESX.Train.resxTrain} />
      <PreviewPanelContent>
        <PlaceholderContent />
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>;
  }

  const train = api.data;
  const trainSegment = api.data.segments.find(segment => segment.id === trainSegmentId);

  const startDateWritten = formatWrittenDayMonthYear(trainSegment.start.dateTimeLocal);
  const endDateWritten = formatWrittenDayMonthYear(trainSegment.end.dateTimeLocal);

  const startTimeWritten = formatTime(trainSegment.start.dateTimeLocal);
  const endTimeWritten = formatTime(trainSegment.end.dateTimeLocal);

  return <>
    <PreviewPanelHeader title={RESX.Train.resxTrain} />
    <PreviewPanelContent>
      {train.artist && <PreviewPanelDetailRow labelTop={site.currentEnvironment.functionGroups.artist.singular}>
        <ArtistPreview
          companyId={train.artist.artistId}
          trigger={<a>{train.artist.name}</a>}
          fallbackIfNoPermission={<>{train.artist.name}</>}
        />
      </PreviewPanelDetailRow>}

      {train.travellers.length > 0 && <PreviewPanelDetailRow labelTop={RESX.Flight.Travellers}>
        <PreviewPanelContactList contacts={train.travellers} />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
        <AppointmentPhase value={train.phase} displayLabel />
      </PreviewPanelDetailRow>

      <PreviewPanelHorizontalDivider />

      <PreviewPanelDepartureArrival
        dateWritten={startDateWritten}
        timeWritten={startTimeWritten}
        addressLine1={trainSegment.start.station.name}
        addressLine2={trainSegment.start.station.address.formatted.join(", ")}
      />

      <JourneySegment
        duration={trainSegment.duration}
        distance={trainSegment.distance}
      />

      <PreviewPanelDepartureArrival
        dateWritten={endDateWritten !== startDateWritten ? endDateWritten : undefined}
        timeWritten={endTimeWritten}
        addressLine1={trainSegment.end.station.name}
        addressLine2={trainSegment.end.station.address.formatted.join(", ")}
      />

      <PreviewPanelHorizontalDivider />

      {trainSegment.trainNumber && <PreviewPanelDetailRow labelTop={RESX.Train.TrainNumber}>
        <span>{trainSegment.trainNumber}</span>
      </PreviewPanelDetailRow>}

      {trainSegment.seats && <PreviewPanelDetailRow labelTop={RESX.Flight.Seats}>
        <span>{trainSegment.seats}</span>
      </PreviewPanelDetailRow>}

      {train.bookingCode && <PreviewPanelDetailRow labelTop={RESX.Flight.BookingCode}>
        <span>{train.bookingCode}</span>
      </PreviewPanelDetailRow>}

    </PreviewPanelContent>
    <PreviewPanelFooter>
      <ButtonPrimary href={routes.train_Detail(train.id).absolute} label={RESX.Buttons.DetailView} />
      {train.permissions.canModify && <ButtonTertiary
        onClick={() => SystemOneLibrary.exports.openModalAndEditSegment(trainSegmentId)}
        label={RESX.Buttons.btnEdit}
      />}
    </PreviewPanelFooter>
  </>
}

export interface TrainSegmentPreviewProps {
  trainSegmentId: number
  trigger: JSX.Element
}

export const TrainSegmentPreview = (props: TrainSegmentPreviewProps) => {
  return <PreviewPanel
    trigger={props.trigger}
    size="medium"
    id={`train-segment-preview-${props.trainSegmentId}`}
  >
    <TrainSegmentPreviewPanel trainSegmentId={props.trainSegmentId} />
  </PreviewPanel>
};